import { useState, useEffect, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, FormControl,
  InputLabel, Select, Grid, Button, Alert, FormHelperText, Divider,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import InfoIcon from "@mui/icons-material/Info";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { ServerConfig } from "../../../connectors/Config";

const API = ServerConfig.AWS.Serverless.API_BASE_URL_2;

// Converte un valore data (stringa/Dayjs) in "YYYY-MM-DD" per l'API, o null.
const toApiDate = (v) => (v && dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : null);
// Converte una stringa data dell'API in oggetto Dayjs per il DatePicker.
const toPickerDate = (v) => (v ? dayjs(v) : null);

/**
 * Form completo anagrafica discente conforme al Decreto D.Dir. 1986/2024.
 * Sezioni: Tipologia discente, Dati anagrafici, Documento d'identità,
 * Libretto matricola Gente di Mare.
 *
 * @param {string} profileId - id del profilo (profiles.id) del discente.
 */
const AnagraficaDiscente = ({ profileId }) => {
  const [profile, setProfile] = useState({});
  const [tipologie, setTipologie] = useState([]);
  const [selectedTipologia, setSelectedTipologia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const loadTipologie = useCallback(async () => {
    try {
      const res = await fetch(`${API}/decreto/tipologie`);
      const json = await res.json();
      const list = Array.isArray(json?.tipologie) ? json.tipologie : [];
      setTipologie(list);
      return list;
    } catch (e) {
      console.error("Errore caricamento tipologie", e);
      return [];
    }
  }, []);

  const loadProfile = useCallback(async (tipList) => {
    if (!profileId) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/decreto/discente?profile_id=${profileId}`);
      const json = await res.json();
      const d = json?.discente || {};
      setProfile(d);
      if (d.tipologia_discente_id) {
        const tip = (tipList || tipologie).find((t) => t.id === d.tipologia_discente_id);
        if (tip) setSelectedTipologia(tip);
      }
    } catch (e) {
      setErrorMessage("Errore nel caricamento dell'anagrafica.");
    } finally {
      setLoading(false);
    }
  }, [profileId, tipologie]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const list = await loadTipologie();
      await loadProfile(list);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const handleTipologiaChange = (tipologiaId) => {
    const tip = tipologie.find((t) => t.id === tipologiaId);
    setSelectedTipologia(tip || null);
    setProfile({ ...profile, tipologia_discente_id: tipologiaId });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const payload = {
        profile_id: profileId,
        first_name: profile.first_name || null,
        last_name: profile.last_name || null,
        fiscal_code: profile.fiscal_code || null,
        data_nascita: toApiDate(profile.data_nascita),
        luogo_nascita: profile.luogo_nascita || null,
        cittadinanza: profile.cittadinanza || null,
        tipologia_discente_id: profile.tipologia_discente_id || null,
        documento_identita_tipo: profile.documento_identita_tipo || null,
        documento_identita_numero: profile.documento_identita_numero || null,
        documento_identita_scadenza: toApiDate(profile.documento_identita_scadenza),
        numero_libretto: profile.numero_libretto || null,
        data_rilascio_libretto: toApiDate(profile.data_rilascio_libretto),
        titolo_professionale: profile.titolo_professionale || null,
      };
      const res = await fetch(`${API}/decreto/discente`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSuccessMessage("Anagrafica aggiornata con successo.");
      } else {
        const err = await res.json().catch(() => ({}));
        setErrorMessage(err?.error || "Errore durante il salvataggio.");
      }
    } catch (e) {
      setErrorMessage("Errore di connessione durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        {/* SEZIONE TIPOLOGIA DISCENTE */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tipologia Discente (Decreto 1986/2024)
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControl fullWidth>
              <InputLabel>Tipologia Discente</InputLabel>
              <Select
                value={profile.tipologia_discente_id || ""}
                onChange={(e) => handleTipologiaChange(e.target.value)}
                label="Tipologia Discente"
              >
                {tipologie.map((tip) => (
                  <MenuItem key={tip.id} value={tip.id}>
                    <strong>{tip.codice}</strong>&nbsp;- {tip.descrizione}
                  </MenuItem>
                ))}
              </Select>
              {selectedTipologia && selectedTipologia.descrizione_completa && (
                <FormHelperText>
                  <InfoIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                  {selectedTipologia.descrizione_completa}
                </FormHelperText>
              )}
            </FormControl>

            {selectedTipologia && Array.isArray(selectedTipologia.corsi_ammessi) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Corsi ammessi:</strong>{" "}
                  {selectedTipologia.corsi_ammessi.length
                    ? selectedTipologia.corsi_ammessi.join(", ")
                    : "Nessuno"}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* SEZIONE DATI ANAGRAFICI */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Dati Anagrafici Completi</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField label="Cognome" fullWidth value={profile.last_name || ""}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Nome" fullWidth value={profile.first_name || ""}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Codice Fiscale" fullWidth value={profile.fiscal_code || ""}
                  onChange={(e) => setProfile({ ...profile, fiscal_code: e.target.value.toUpperCase() })}
                  inputProps={{ maxLength: 16 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker label="Data di Nascita" value={toPickerDate(profile.data_nascita)}
                  onChange={(date) => setProfile({ ...profile, data_nascita: date })}
                  slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Luogo di Nascita" fullWidth value={profile.luogo_nascita || ""}
                  onChange={(e) => setProfile({ ...profile, luogo_nascita: e.target.value })}
                  placeholder="Comune (Provincia)" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Cittadinanza" fullWidth value={profile.cittadinanza || ""}
                  onChange={(e) => setProfile({ ...profile, cittadinanza: e.target.value })} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* SEZIONE DOCUMENTO IDENTITÀ */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Documento di Identità</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Documento</InputLabel>
                  <Select value={profile.documento_identita_tipo || ""}
                    onChange={(e) => setProfile({ ...profile, documento_identita_tipo: e.target.value })}
                    label="Tipo Documento">
                    <MenuItem value="CI">Carta d'Identità</MenuItem>
                    <MenuItem value="PASS">Passaporto</MenuItem>
                    <MenuItem value="PAT">Patente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Numero Documento" fullWidth
                  value={profile.documento_identita_numero || ""}
                  onChange={(e) => setProfile({ ...profile, documento_identita_numero: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker label="Data Scadenza"
                  value={toPickerDate(profile.documento_identita_scadenza)}
                  onChange={(date) => setProfile({ ...profile, documento_identita_scadenza: date })}
                  slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* SEZIONE LIBRETTO MATRICOLA */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Libretto Matricola Gente di Mare</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField label="Numero Libretto" fullWidth value={profile.numero_libretto || ""}
                  onChange={(e) => setProfile({ ...profile, numero_libretto: e.target.value })}
                  placeholder="Es: 123456/2020" />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker label="Data Rilascio"
                  value={toPickerDate(profile.data_rilascio_libretto)}
                  onChange={(date) => setProfile({ ...profile, data_rilascio_libretto: date })}
                  slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Titolo Professionale" fullWidth
                  value={profile.titolo_professionale || ""}
                  onChange={(e) => setProfile({ ...profile, titolo_professionale: e.target.value })}
                  placeholder="Es: Allievo Ufficiale di Coperta" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="contained" size="large" startIcon={<SaveIcon />}
            onClick={handleSave} disabled={saving}>
            {saving ? "Salvataggio..." : "Salva Anagrafica"}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AnagraficaDiscente;
